/**
 * Cart store factory. Lifted from
 * `apps/customer-web/src/store/cart-store.ts` and parameterized on
 * `KeyValueStore` so each app supplies its own platform persistence
 * (web's `localStorage` adapter, mobile's AsyncStorage adapter).
 *
 * `KeyValueStore` is async (matches RN AsyncStorage natively); zustand
 * `persist` middleware accepts the async signature directly via
 * `createJSONStorage`.
 */
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { combinedPurchaseExceedsDailyCap, formatCombinedLimitExceededMessage, purchaseDeltasFromCartLines, } from "./purchase-deltas";
function cloneLines(slug, carts) {
    return [...(carts[slug] ?? [])];
}
function menuItemToLineDraft(item, quantity) {
    return {
        metrcPackageId: item.metrcPackageId,
        metrcPackageUid: item.metrcPackageUid,
        productName: item.productName,
        productCategory: item.productCategory,
        quantityUnit: item.quantityUnit,
        thcMgPerUnit: item.thcMgPerUnit,
        unitPriceCents: item.unitPriceCents,
        maxQuantity: item.quantityAvailable,
        productImageUrl: item.productImageUrl ?? null,
        quantity,
    };
}
function assertWithinStock(lines) {
    for (const li of lines) {
        const q = Number(li.quantity);
        const cap = Number(li.maxQuantity);
        if (!Number.isFinite(q) || q <= 0)
            return false;
        if (!Number.isFinite(cap) || q > cap + 1e-9)
            return false;
    }
    return true;
}
function assertWithinCoLimits(lines) {
    const deltas = purchaseDeltasFromCartLines(lines);
    if (!combinedPurchaseExceedsDailyCap(deltas))
        return null;
    return formatCombinedLimitExceededMessage(deltas);
}
export function createCartStore(opts) {
    const { storage, persistKey = "thedrop-cwa-cart-v1" } = opts;
    return create()(persist((set, get) => ({
        carts: {},
        addOrIncrement: (dispensarySlug, item, delta = 1) => {
            if (item.soldOut) {
                return { ok: false, reason: "This product is sold out." };
            }
            const d = Number(delta);
            if (!Number.isFinite(d) || d <= 0) {
                return { ok: false, reason: "Invalid quantity." };
            }
            const lines = cloneLines(dispensarySlug, get().carts);
            const idx = lines.findIndex((l) => l.metrcPackageId === item.metrcPackageId);
            const prevQty = idx >= 0 ? Number(lines[idx].quantity) : 0;
            if (!Number.isFinite(prevQty) || prevQty < 0) {
                return { ok: false, reason: "Invalid cart state." };
            }
            const nextQty = prevQty + d;
            const cap = Number(item.quantityAvailable);
            if (!Number.isFinite(cap) || nextQty > cap + 1e-9) {
                return { ok: false, reason: "Not enough inventory for that quantity." };
            }
            const draft = menuItemToLineDraft(item, String(nextQty));
            if (idx >= 0) {
                lines[idx] = draft;
            }
            else {
                lines.push(draft);
            }
            if (!assertWithinStock(lines)) {
                return { ok: false, reason: "Not enough inventory for that quantity." };
            }
            const limitMsg = assertWithinCoLimits(lines);
            if (limitMsg) {
                return { ok: false, reason: limitMsg };
            }
            set((s) => ({
                carts: { ...s.carts, [dispensarySlug]: lines },
            }));
            return { ok: true };
        },
        reconcileCartWithMenu: (dispensarySlug, menuItems) => {
            const prev = get().carts[dispensarySlug];
            if (!prev || prev.length === 0) {
                return { changed: false, removed: [], clamped: [] };
            }
            const byId = new Map(menuItems.map((i) => [i.metrcPackageId, i]));
            const next = [];
            const removed = [];
            const clamped = [];
            for (const line of prev) {
                const item = byId.get(line.metrcPackageId);
                if (!item || item.soldOut) {
                    removed.push({ productName: line.productName });
                    continue;
                }
                const cap = Number(item.quantityAvailable);
                if (!Number.isFinite(cap) || cap <= 0) {
                    removed.push({ productName: line.productName });
                    continue;
                }
                let qty = Number(line.quantity);
                if (!Number.isFinite(qty) || qty <= 0) {
                    removed.push({ productName: line.productName });
                    continue;
                }
                const originalQty = qty;
                if (qty > cap) {
                    qty = cap;
                }
                if (qty !== originalQty) {
                    clamped.push({
                        productName: line.productName,
                        from: String(originalQty),
                        to: String(qty),
                    });
                }
                next.push({
                    ...line,
                    productCategory: item.productCategory,
                    quantityUnit: item.quantityUnit,
                    thcMgPerUnit: item.thcMgPerUnit,
                    unitPriceCents: item.unitPriceCents,
                    maxQuantity: item.quantityAvailable,
                    productImageUrl: item.productImageUrl ?? line.productImageUrl ?? null,
                    quantity: String(qty),
                });
            }
            const same = next.length === prev.length &&
                next.every((l, i) => {
                    const o = prev[i];
                    return (l.metrcPackageId === o.metrcPackageId &&
                        l.quantity === o.quantity &&
                        l.maxQuantity === o.maxQuantity &&
                        l.unitPriceCents === o.unitPriceCents);
                });
            if (same) {
                return { changed: false, removed: [], clamped: [] };
            }
            set((s) => ({
                carts: { ...s.carts, [dispensarySlug]: next },
            }));
            return { changed: true, removed, clamped };
        },
        setLineQuantity: (dispensarySlug, metrcPackageId, quantity) => {
            const q = Number(quantity);
            if (!Number.isFinite(q) || q <= 0) {
                return { ok: false, reason: "Quantity must be a positive number." };
            }
            const lines = cloneLines(dispensarySlug, get().carts);
            const idx = lines.findIndex((l) => l.metrcPackageId === metrcPackageId);
            if (idx < 0) {
                return { ok: false, reason: "Line not in cart." };
            }
            const next = { ...lines[idx], quantity: String(q) };
            const cap = Number(next.maxQuantity);
            if (!Number.isFinite(cap) || q > cap + 1e-9) {
                return { ok: false, reason: "Not enough inventory for that quantity." };
            }
            lines[idx] = next;
            if (!assertWithinStock(lines)) {
                return { ok: false, reason: "Not enough inventory for that quantity." };
            }
            const limitMsg = assertWithinCoLimits(lines);
            if (limitMsg) {
                return { ok: false, reason: limitMsg };
            }
            set((s) => ({
                carts: { ...s.carts, [dispensarySlug]: lines },
            }));
            return { ok: true };
        },
        removeLine: (dispensarySlug, metrcPackageId) => {
            set((s) => {
                const prev = s.carts[dispensarySlug] ?? [];
                const next = prev.filter((l) => l.metrcPackageId !== metrcPackageId);
                return { carts: { ...s.carts, [dispensarySlug]: next } };
            });
        },
        clearCart: (dispensarySlug) => {
            set((s) => {
                const next = { ...s.carts };
                delete next[dispensarySlug];
                return { carts: next };
            });
        },
        clearAllCarts: () => set({ carts: {} }),
    }), {
        name: persistKey,
        storage: createJSONStorage(() => storage),
        partialize: (s) => ({ carts: s.carts }),
    }));
}
export function lineSubtotalCents(line) {
    return Math.round(line.unitPriceCents * Number(line.quantity));
}
export function cartSubtotalCents(lines) {
    return lines.reduce((sum, l) => sum + lineSubtotalCents(l), 0);
}
/**
 * Human-readable banner text for a `reconcileCartWithMenu` summary.
 * Returns `null` when nothing changed.
 */
export function formatReconcileSummary(summary) {
    if (!summary.changed)
        return null;
    const parts = [];
    if (summary.removed.length > 0) {
        const names = summary.removed.map((r) => r.productName).join(", ");
        parts.push(`Removed (out of stock): ${names}.`);
    }
    if (summary.clamped.length > 0) {
        const names = summary.clamped
            .map((c) => `${c.productName} (${c.from} → ${c.to})`)
            .join(", ");
        parts.push(`Reduced to match available stock: ${names}.`);
    }
    if (parts.length === 0) {
        return "Cart updated to match the latest inventory.";
    }
    return parts.join(" ");
}
//# sourceMappingURL=create-cart-store.js.map